// client/src/components/DynamicResultsTable.js
// A reusable table component that can display any query result with dynamic columns
import React, { useState, useMemo } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaDownload, FaChevronLeft, FaChevronRight, FaCopy, FaCheck } from 'react-icons/fa';

const DynamicResultsTable = ({ 
  data = [], 
  fields = [], 
  onExport,
  pageSize = 50,
  showPagination = true,
  maxHeight = '500px',
  title = 'Results'
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [copiedCell, setCopiedCell] = useState(null);

  // Derive columns from fields or first row
  const columns = useMemo(() => {
    if (fields && fields.length > 0) return fields;
    if (data && data.length > 0) return Object.keys(data[0]);
    return [];
  }, [fields, data]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField || !data) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle nulls
      if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  }, [data, sortField, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, showPagination]);

  const totalPages = Math.ceil((data?.length || 0) / pageSize);

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle row selection
  const toggleRowSelection = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map((_, i) => i)));
    }
  };

  // Copy cell value
  const handleCopyCell = async (value, cellId) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ''));
      setCopiedCell(cellId);
      setTimeout(() => setCopiedCell(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format cell value for display
  const formatCellValue = (value, field) => {
    if (value === null || value === undefined) return <span className="text-gray-400 italic">null</span>;
    if (typeof value === 'boolean') return value ? '✓' : '✗';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    
    // Truncate long strings
    const strValue = String(value);
    if (strValue.length > 100) {
      return (
        <span title={strValue}>
          {strValue.substring(0, 100)}...
        </span>
      );
    }
    return strValue;
  };

  // Format column header
  const formatHeader = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="font-medium text-gray-900">{title}</h3>
          <span className="text-sm text-gray-500">
            {data.length} {data.length === 1 ? 'row' : 'rows'}
            {data.length >= 1000 && <span className="text-amber-600 ml-1">(limit reached)</span>}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.size > 0 && (
            <span className="text-sm text-blue-600">
              {selectedRows.size} selected
            </span>
          )}
          {onExport && (
            <button
              onClick={onExport}
              className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <FaDownload className="mr-1.5" size={12} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="overflow-auto" style={{ maxHeight }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {/* Selection checkbox column */}
              <th className="px-3 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {/* Row number column */}
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                #
              </th>
              {/* Dynamic columns */}
              {columns.map((field) => (
                <th
                  key={field}
                  onClick={() => handleSort(field)}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                >
                  <div className="flex items-center space-x-1">
                    <span>{formatHeader(field)}</span>
                    {sortField === field ? (
                      sortDirection === 'asc' ? <FaSortUp className="text-blue-600" /> : <FaSortDown className="text-blue-600" />
                    ) : (
                      <FaSort className="text-gray-400" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((row, rowIndex) => {
              const globalIndex = (currentPage - 1) * pageSize + rowIndex;
              return (
                <tr
                  key={rowIndex}
                  className={`${selectedRows.has(rowIndex) ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => toggleRowSelection(rowIndex)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {globalIndex + 1}
                  </td>
                  {columns.map((field) => {
                    const cellId = `${rowIndex}-${field}`;
                    return (
                      <td
                        key={field}
                        className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate group relative"
                      >
                        <div className="flex items-center">
                          <span className="truncate">{formatCellValue(row[field], field)}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyCell(row[field], cellId);
                            }}
                            className="ml-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity"
                            title="Copy value"
                          >
                            {copiedCell === cellId ? (
                              <FaCheck className="text-green-500" size={12} />
                            ) : (
                              <FaCopy size={12} />
                            )}
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, data.length)} of {data.length}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaChevronLeft size={14} />
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicResultsTable;
